import React, { useState, useEffect } from "react";
import MuxPlayer from "@mux/mux-player-react";
import { Table, Spinner, Card, CardImg, Button, Input, InputGroup, InputGroupText } from "reactstrap";
import "./Playlist.css";
import "../icons.css";
import {
  addToPlaylist,
  removeFromPlaylist,
  getSinglePlaylist,
  getPlaylistVideos,
  batchUpdateVideoOrder,
} from "../services/playlist.service";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableRow = ({ record, removeFromPlaylistClick, changePlayback, is_hls }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: record.playlist_video_id });

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f0f0f0' : (record.isPending ? '#fffacd' : 'white'),
  };

  const gripperStyle = {
    cursor: 'grab',
    fontSize: '20px',
    color: '#888',
    userSelect: 'none',
    padding: '0.5rem',
    textAlign: 'center',
  };

  return (
    <tr ref={setNodeRef} style={rowStyle}>
      <td style={gripperStyle} {...attributes} {...listeners}>
        ⋮⋮
      </td>
      <td>{record.title}</td>
      <td>{record.artist}</td>
      <td>
        {is_hls(record.video_url) ? (
          <Button
            className="view-icon btn"
            title="view video"
            onClick={(e) => {
              e.stopPropagation();
              changePlayback(record.video_url, record.title, e);
            }}
          />
        ) : (
          <a
            href={record.video_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="view-icon btn" title="view video"></div>
          </a>
        )}
      </td>
      <td>
        <div
          className="cross-icon btn"
          onClick={(e) => {
            e.stopPropagation();
            removeFromPlaylistClick(record.video_id, e);
          }}
        ></div>
      </td>
    </tr>
  );
};

const ManagePlaylist = (props) => {
  const [otherVideos, setVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [pendingAdditions, setPendingAdditions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [playlist_id, setPlaylistID] = useState(null);
  const [playlist, setPlaylist] = useState({});
  const [videoInfo, setVideoInfo] = useState({ playbackId: "", title: "" });
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [search, setSearch] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setPlaylistVideos((videos) => {
        const oldIndex = videos.findIndex(v => v.playlist_video_id === active.id);
        const newIndex = videos.findIndex(v => v.playlist_video_id === over.id);
        const reordered = arrayMove(videos, oldIndex, newIndex);
        setHasChanges(true);
        return reordered;
      });
    }
  };

  const handleSubmitOrder = async () => {
    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // First, add all pending videos to the playlist
      if (pendingAdditions.length > 0) {
        for (const pendingVideo of pendingAdditions) {
          await addToPlaylist(pendingVideo.video_id, playlist_id);
        }
      }

      // Then update the order of all videos (if there were any drag-drop changes)
      const allVideos = [...playlistVideos, ...pendingAdditions];
      if (allVideos.length > 0) {
        const videoOrders = playlistVideos.map((video, index) => ({
          playlist_video_id: video.playlist_video_id,
          order: index
        }));

        if (videoOrders.length > 0) {
          await batchUpdateVideoOrder(playlist_id, videoOrders);
        }
      }

      // Success! Refresh the page to show updated data
      setSuccessMessage("Changes saved successfully! Refreshing...");
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error("Failed to save changes:", error);
      setErrorMessage("Failed to save changes. Please try again.");
      setIsSaving(false);
    }
  };

  const addToPlaylistClick = (v_id, e) => {
    // Find the video in otherVideos
    const videoToAdd = otherVideos.find((ele) => ele.video_id === v_id);

    if (videoToAdd) {
      // Add to pending additions with a temporary ID for drag-and-drop
      const pendingVideo = {
        ...videoToAdd,
        playlist_video_id: `pending_${v_id}`,
        isPending: true,
        order: playlistVideos.length + pendingAdditions.length
      };

      setPendingAdditions([...pendingAdditions, pendingVideo]);

      // Remove from available videos
      setVideos(otherVideos.filter((ele) => ele.video_id !== v_id));
      setAllVideos(allVideos.filter((ele) => ele.video_id !== v_id));

      setHasChanges(true);
      setErrorMessage("");
    }
  };

  const removeFromPlaylistClick = (v_id, e) => {
    // Check if this is a pending video
    const pendingVideo = pendingAdditions.find((ele) => ele.video_id === v_id);

    if (pendingVideo) {
      // Just remove from pending, don't call API
      setPendingAdditions(pendingAdditions.filter((ele) => ele.video_id !== v_id));

      // Add back to available videos
      const videoToRestore = { ...pendingVideo };
      delete videoToRestore.isPending;
      delete videoToRestore.playlist_video_id;

      setVideos([videoToRestore, ...otherVideos]);
      setAllVideos([videoToRestore, ...allVideos]);

      if (pendingAdditions.length === 1) {
        setHasChanges(false);
      }
    } else {
      // This is a saved video, call the API
      removeFromPlaylist(v_id, playlist_id).then(() => {
        const record = [];
        setPlaylistVideos(
          playlistVideos.filter((ele) => {
            if (ele.video_id === v_id) {
              record.push(ele);
            }
            return ele.video_id !== v_id;
          })
        );
        setVideos([record[0], ...otherVideos]);
        setAllVideos([record[0], ...allVideos]);
      });
    }
  };

  const changePlayback = (url, title, e) => {
    if (!isPlayerOpen) {
      setIsPlayerOpen(true);
    }
    let firInd = url.lastIndexOf("/");
    let ltInd = url.lastIndexOf(".");
    const urlPlaybackId = url.substring(firInd + 1, ltInd);
    console.log(urlPlaybackId);
    setVideoInfo({ playbackId: urlPlaybackId, title: title });
  };

  let IsPlayer = ({ children }) => {
    return isPlayerOpen ? children : <></>;
  };

  const is_hls = (url) => {
    try {
      return Boolean(url.includes(".m3u8"));
    } catch (error) {
      console.error("Error checking if URL is HLS:", error);
      return false;
    }
  };

  const handleSearch = (e) => {
    if (e.target.id === "search") {
      setSearch(e.target.value)
      if (e.target.value.length > 2) {
        let text=e.target.value
        const result = allVideos.filter((video) => (video.artist + " " + video.title + " " + video.record_label).toLowerCase()
          .includes(text.toLowerCase()));
        setVideos(result);
      }
    }
  };

  useEffect(() => {
    const id = window.location.href.split("/");

    getSinglePlaylist(parseInt(id[id.length - 1])).then((res) => {
      setPlaylist(res.data[0]);
      setPlaylistID(res.data[0].id);
    });
    getPlaylistVideos(parseInt(id[id.length - 1]))
      .then((res) => {
        const videosInPlaylist = res.data.filter((ele) => {
          return ele.inplaylist !== null;
        });
        setPlaylistVideos(videosInPlaylist);
        setOriginalOrder([...videosInPlaylist]);

        setAllVideos(
          res.data.filter((ele) => {
            return ele.inplaylist === null;
          })
        );

        setVideos(
          res.data.filter((ele) => {
            return ele.inplaylist === null;
          }).slice(0, 100)
        );
      })
      .catch((err) => console.log(err));
  }, []);

  return (
    <div style={{ margin: "1rem 2rem" }}>
      <IsPlayer>
        <div
          style={{
            width: "1076px",
            position: "sticky",
            top: "9rem",
            marginLeft: "auto",
            right: "0",
          }}
        >
          <div
            style={{
              width: "533px",
              position: "fixed",
              marginLeft: "auto",
              right: "0",
              top: "9rem",
            }}
          >
            <h5
              style={{
                position: "fixed",
                marginLeft: "2rem",
                marginRight: "auto",
                color: "white",
              }}
            >
              {videoInfo.title}
            </h5>
            <Button
              style={{
                position: "fixed",
                marginLeft: "auto",
                right: "2rem",
                background: "transparent",
                color: "white",
                fontSize: "22px",
              }}
              onClick={() => {
                setIsPlayerOpen(false);
              }}
            >
              x
            </Button>

            <MuxPlayer
              style={{
                zIndex: "-1",
                position: "relative",
                display: "block",
                height: "300px",
                width: "533px",
                marginLeft: "auto",
                right: "0",
              }}
              streamType="on-demand"
              playbackId={videoInfo.playbackId}
              metadata={{
                video_id: "video-id-54321",
                video_title: `${videoInfo.title}`,
                viewer_user_id: "user-id-007",
              }}
            />
          </div>
        </div>
      </IsPlayer>
      <div>
        <p
          style={{
            fontSize: "25px",
            fontWeight: "600",
            marginLeft: "1.5rem",
          }}
        >
          Manage Playlist
        </p>
      </div>
      <div>
        {playlist_id ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', alignItems: 'center' }}>
              <div>
                {hasChanges && !isSaving && (
                  <span style={{ color: '#28a745', fontWeight: 'bold', marginRight: '1rem' }}>
                    ● {pendingAdditions.length > 0 ? `${pendingAdditions.length} pending addition(s)` : 'Unsaved changes'}
                  </span>
                )}
                {successMessage && (
                  <span style={{ color: '#28a745', marginRight: '1rem' }}>
                    {successMessage}
                  </span>
                )}
                {errorMessage && (
                  <span style={{ color: 'red', marginRight: '1rem' }}>
                    {errorMessage}
                  </span>
                )}
                <Button
                  color="success"
                  onClick={handleSubmitOrder}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? "SAVING..." : "SAVE CHANGES"}
                </Button>
              </div>
            </div>
            <div className="playlist-container">
              <Card className="playlist-card">
                <CardImg alt="Card image cap" src={playlist.image_url} top className="playlist-img" />
                <p className="playlist-heading">{playlist.name}</p>
              </Card>
              <div>
                {pendingAdditions.length > 0 && (
                  <div style={{ marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#fffacd', border: '1px solid #ffd700', borderRadius: '4px' }}>
                    <strong>💡 Tip:</strong> Videos highlighted in yellow are pending additions. Click "SAVE CHANGES" to add them permanently.
                  </div>
                )}
                <div className="table-container">
                {(playlistVideos.length > 0 || pendingAdditions.length > 0) ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <Table responsive striped style={{ background: "white", tableLayout: "fixed" }}>
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th style={{ width: "200px" }}>Title</th>
                          <th style={{ width: "200px" }}>Artist</th>
                          <th style={{ width: "70px" }}>Video Link</th>
                          <th style={{ width: "70px" }}>Action</th>
                        </tr>
                      </thead>
                      <SortableContext
                        items={[...playlistVideos, ...pendingAdditions].map(v => v.playlist_video_id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <tbody>
                          {playlistVideos.map((record) => (
                            <SortableRow
                              key={record.playlist_video_id}
                              record={record}
                              removeFromPlaylistClick={removeFromPlaylistClick}
                              changePlayback={changePlayback}
                              is_hls={is_hls}
                            />
                          ))}
                          {pendingAdditions.map((record) => (
                            <SortableRow
                              key={record.playlist_video_id}
                              record={record}
                              removeFromPlaylistClick={removeFromPlaylistClick}
                              changePlayback={changePlayback}
                              is_hls={is_hls}
                            />
                          ))}
                        </tbody>
                      </SortableContext>
                    </Table>
                  </DndContext>
                ) : (
                  <div className="no_playlist_msg">
                    <h4>No Video in this Playlist</h4>
                  </div>
                )}
              </div>
              <div>
                  <InputGroup>
                    <InputGroupText style={{ color: "white" }}>Search </InputGroupText>
                    <Input onChange={handleSearch} value={search} placeholder="Search by artist, title, or record label" id="search" />
                  </InputGroup>
              </div>
              <br />
              <div className="table-container">
                <Table responsive striped style={{ background: "white", tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "200px" }}>Title</th>
                      <th style={{ width: "200px" }}>Artist</th>
                      <th style={{ width: "70px" }}>Video Link</th>
                      <th style={{ width: "70px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherVideos
                      .sort((a, b) =>
                        b.video_id < a.video_id ? -1 : b.video_id > a.video_id ? 1 : 0
                      )
                      .map((record) => {
                        return (
                          <tr key={record.video_id}>
                            <td>{record.title}</td>
                            <td>{record.artist}</td>
                            <td>
                              {is_hls(record.video_url) ? (
                                <Button
                                  className="view-icon btn"
                                  title="view video"
                                  onClick={changePlayback.bind(
                                    this,
                                    record.video_url,
                                    record.title
                                  )}
                                />
                              ) : (
                                <a
                                  href={record.video_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <div className="view-icon btn" title="view video"></div>
                                </a>
                              )}
                            </td>
                            <td>
                              <div
                                className="plus-icon btn"
                                onClick={addToPlaylistClick.bind(this, record.video_id)}
                              ></div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </div>
            </div>
            </div>
          </div>
        ) : (
          <Spinner color="warning">Loading....</Spinner>
        )}
      </div>
    </div>
  );
};

export default ManagePlaylist;
